"use client";

// Fetches the public snow report summary (GET /api/snow-checkins, counts only, edge cached
// for a minute) after mount, so the statically rendered page makes no extra database read
// while it renders. Shows nothing while loading or when the summary is unavailable.

import { useEffect, useState } from "react";
import type { SnowCheckinPublicSummary } from "@/lib/conditions/snow-checkins";
import { SnowCheckinSummary } from "@/app/_components/conditions/SnowCheckinSummary";

export const SNOW_CHECKIN_SUMMARY_TIMEOUT_MS = 10_000;

function isCountRecord(value: unknown): value is Record<string, number> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    && Object.values(value as Record<string, unknown>).every((n) => typeof n === "number" && Number.isFinite(n) && n >= 0);
}

/** Narrow check of the API payload; anything else (an error body, a bad shape) reads as unavailable. */
export function parseSnowCheckinSummaryPayload(payload: unknown): SnowCheckinPublicSummary | null {
  if (typeof payload !== "object" || payload === null) return null;
  const p = payload as Record<string, unknown>;
  const rating = p.rating as Record<string, unknown> | null | undefined;
  if (
    typeof p.locationId !== "string"
    || typeof p.windowHours !== "number"
    || typeof p.total !== "number" || !Number.isInteger(p.total) || p.total < 0
    || !isCountRecord(p.cover)
    || !isCountRecord(p.surface)
    || typeof rating !== "object" || rating === null
    || typeof rating.count !== "number"
    || !(rating.average === null || typeof rating.average === "number")
    || typeof p.breakdownWithheld !== "boolean"
    || typeof p.truncated !== "boolean"
  ) return null;
  return payload as SnowCheckinPublicSummary;
}

export function SnowCheckinSummaryLive({ locationId }: { locationId: string }) {
  const [summary, setSummary] = useState<SnowCheckinPublicSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SNOW_CHECKIN_SUMMARY_TIMEOUT_MS);
    fetch(`/api/snow-checkins?locationId=${encodeURIComponent(locationId)}`, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload: unknown) => {
        if (!cancelled) setSummary(parseSnowCheckinSummaryPayload(payload));
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      })
      .finally(() => clearTimeout(timer));
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [locationId]);

  if (!summary) return null;
  return (
    <div className="rounded-[14px] border border-border-light bg-white p-4" data-snow-checkin-summary="">
      <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">Snow reports</h3>
      <SnowCheckinSummary summary={summary} />
    </div>
  );
}
